import React from "react"

import { FileUp, Loader2, X } from "lucide-react"

import DocumentPreview from "@/components/application/DocumentPreview"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"

interface TachoCardSectionProps {
  tachoFrontFile: File | null
  setTachoFrontFile: (file: File | null) => void
  tachoFrontPreview: string | null
  setTachoFrontPreview: (preview: string | null) => void
  tachoBackFile: File | null
  setTachoBackFile: (file: File | null) => void
  tachoBackPreview: string | null
  setTachoBackPreview: (preview: string | null) => void
  tachoCardNumber: string
  setTachoCardNumber: (value: string) => void
  onSubmit: () => void
  isLoading?: boolean
}

const TachoCardSection: React.FC<TachoCardSectionProps> = ({
  tachoFrontFile,
  setTachoFrontFile,
  tachoFrontPreview,
  setTachoFrontPreview,
  tachoBackFile,
  setTachoBackFile,
  tachoBackPreview,
  setTachoBackPreview,
  tachoCardNumber,
  setTachoCardNumber,
  onSubmit,
  isLoading = false,
}) => {
  const { toast } = useToast()

  const handleFrontFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0]
      setTachoFrontFile(f)
      const fileUrl = URL.createObjectURL(f)
      setTachoFrontPreview(fileUrl)
    }
  }

  const handleBackFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0]
      setTachoBackFile(f)
      const fileUrl = URL.createObjectURL(f)
      setTachoBackPreview(fileUrl)
    }
  }

  const triggerFileInput = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement
    if (input) {
      input.click()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!tachoCardNumber.trim()) {
      toast({
        title: "Missing Number",
        description: "Please enter your Tachograph card number.",
        variant: "destructive",
      })
      return
    }
    const hasFront = Boolean(tachoFrontFile) || Boolean(tachoFrontPreview)
    const hasBack = Boolean(tachoBackFile) || Boolean(tachoBackPreview)
    if (!hasFront || !hasBack) {
      toast({
        title: "Missing File",
        description: "Please upload both front and back of your Tachograph card.",
        variant: "destructive",
      })
      return
    }
    onSubmit()
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tacho Card Upload</CardTitle>
        <CardDescription>
          Upload clear images or scans of both sides of your Tachograph card.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="tachoCardNumber">Tachograph Card Number</Label>
            <Input
              id="tachoCardNumber"
              type="text"
              value={tachoCardNumber}
              onChange={(e) => setTachoCardNumber(e.target.value)}
              placeholder="Enter your Tacho card number"
              required
            />
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tachoFrontFile">Tacho Front</Label>
              <div
                className="flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 p-4 transition-colors hover:border-gray-400"
                onClick={() => triggerFileInput("tachoFrontFile")}
              >
                {tachoFrontPreview ? (
                  <div className="flex flex-col items-center space-y-2">
                    <DocumentPreview
                      src={tachoFrontPreview}
                      alt="Tacho front preview"
                      file={tachoFrontFile}
                      imageClassName="max-h-32 object-contain"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setTachoFrontFile(null)
                        setTachoFrontPreview(null)
                      }}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <FileUp className="mb-2 h-8 w-8 text-gray-400" />
                    <p className="text-sm text-gray-500">Click to upload Tacho front</p>
                  </>
                )}
              </div>
              <Input
                id="tachoFrontFile"
                type="file"
                className="hidden"
                onChange={handleFrontFileChange}
                accept="image/png,image/jpeg,application/pdf"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tachoBackFile">Tacho Back</Label>
              <div
                className="flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 p-4 transition-colors hover:border-gray-400"
                onClick={() => triggerFileInput("tachoBackFile")}
              >
                {tachoBackPreview ? (
                  <div className="flex flex-col items-center space-y-2">
                    <DocumentPreview
                      src={tachoBackPreview}
                      alt="Tacho back preview"
                      file={tachoBackFile}
                      imageClassName="max-h-32 object-contain"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setTachoBackFile(null)
                        setTachoBackPreview(null)
                      }}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <>
                    <FileUp className="mb-2 h-8 w-8 text-gray-400" />
                    <p className="text-sm text-gray-500">Click to upload Tacho back</p>
                  </>
                )}
              </div>
              <Input
                id="tachoBackFile"
                type="file"
                className="hidden"
                onChange={handleBackFileChange}
                accept="image/png,image/jpeg,application/pdf"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-honeycomb text-white hover:bg-amber-500"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Tacho Card"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default TachoCardSection
